import RBAC_CONTRACT from "../admin/rbac-contract.json";

/**
 * ORGANIZATION (academy) roles — axis B of Aula's permission model.
 *
 * Note: these are NOT the platform roles from `admin/capabilities.ts`. That
 * `admin` exists on both axes is a naming coincidence, not an equivalence: a
 * platform `super_admin` means nothing inside an academy, and an academy `owner`
 * has no back-office capabilities. The rank here is comparable only WITHIN the
 * same organization.
 *
 * The order and capabilities come from the shared contract
 * (`admin/rbac-contract.json`), which is also the one Rust
 * (`auth::rbac::org`) reads in its tests. One source, two runtimes.
 */
export type OrganizationRole = "admin" | "member" | "owner" | "viewer";

export const ORGANIZATION_ROLES: OrganizationRole[] = ["owner", "admin", "member", "viewer"];

export function isOrganizationRole(role: string): role is OrganizationRole {
  return (ORGANIZATION_ROLES as readonly string[]).includes(role);
}

/**
 * Narrows an arbitrary value to a known organization role, or `null`.
 *
 * It is written as a guard rather than an assertion (`as OrganizationRole`)
 * deliberately: an assertion tells the compiler "trust me" about data from the
 * server, and here that data decides permissions. An unknown role must fall to
 * `null` and have no access, not slip through via an assertion.
 */
function toOrgRole(role: string | null | undefined): OrganizationRole | null {
  return typeof role === "string" && isOrganizationRole(role) ? role : null;
}

/** Rank within an organization, read from the contract (not copied). */
const ORG_ROLE_RANK: Record<OrganizationRole, number> = {
  owner: RBAC_CONTRACT.orgRoles.owner.rank,
  admin: RBAC_CONTRACT.orgRoles.admin.rank,
  member: RBAC_CONTRACT.orgRoles.member.rank,
  viewer: RBAC_CONTRACT.orgRoles.viewer.rank,
};

/** Can `actorRole` manage members/invitations in its organization? */
export function canManageOrgMembers(role: string | null | undefined): boolean {
  const actor = toOrgRole(role);
  return actor === null ? false : RBAC_CONTRACT.orgRoles[actor].manageMembers;
}

/**
 * Organization roles that `actorRole` can GRANT (invite or assign).
 *
 * Mirror of `can_grant_org_role`. Without this, the UI offered `owner` to an
 * academy `admin` and the backend accepted it through the invitation path:
 * invite your own email with `role: "owner"`, accept, and become the owner.
 */
export function assignableOrgRolesFor(
  actorRole: string | null | undefined,
): readonly OrganizationRole[] {
  const actor = toOrgRole(actorRole);
  if (actor === null || !canManageOrgMembers(actor)) {
    return [];
  }
  const actorRank = ORG_ROLE_RANK[actor];
  return ORGANIZATION_ROLES.filter((role) => ORG_ROLE_RANK[role] <= actorRank);
}

/**
 * Can `actorRole` administer (remove / change the role of) a member whose
 * current role is `targetRole`?
 *
 * Unlike on the platform axis, PEERS can administer each other here
 * (`administersPeers: true` in the contract): this is behavior the product
 * already had. What is not allowed is acting upward.
 */
export function canAdministerOrgMember(
  actorRole: string | null | undefined,
  targetRole: string | null | undefined,
): boolean {
  const actor = toOrgRole(actorRole);
  if (actor === null || !canManageOrgMembers(actor)) {
    return false;
  }
  // An unknown target role is treated as the LOWEST: never as a
  // shield that makes it untouchable.
  const target = toOrgRole(targetRole) ?? "viewer";
  return ORG_ROLE_RANK[target] <= ORG_ROLE_RANK[actor];
}
